import {
  VERBS,
  XAPI_STATEMENT_TEMPLATES,
  countWords,
  getContentCategoryFromPath
} from '../utils'

/**
 * @typedef {object} ScrollStatementPayload
 * @property {object} content - The content the user is interacting with.
 * @property {number} scrollPosition - The scroll position.
 * @property {object} tenant - The tenant.
 * @property {number} scrollHeight - The page scroll height.
 * @property {number} engagementTime - The time the user has spent interacting with the content.
 * @property {string} playDuration - The duration the user has played on a content video/audio. This value is in seconds.
 * @property {string} playableMediaType - The media type.
 */
/**
 * Create Prime statement.
 * @param {ScrollStatementPayload} payload
 */
const createPrimeStatement = (payload) => {
  const statement = buildStatement(payload)
  const { content } = payload

  if (
    content?.legacyContent?.source === 'prime' &&
    content?.legacyContent?.id
  ) {
    statement.activity.extensions.pageId = content.legacyContent.id
  }

  return statement
}

/**
 * Create Helix statement.
 * @param {ScrollStatementPayload} payload
 */
const createHelixStatement = (payload) => {
  const statement = buildStatement(payload)
  const { content } = payload

  if (
    content?.legacyContent?.source === 'helix' &&
    content?.legacyContent?.id
  ) {
    statement.activity.extensions.pageId = content.legacyContent.id
  }

  return statement
}

/**
 * Create an XAPI event payload.
 * @param {ScrollStatementPayload} payload - XAPI payload.
 */
const buildStatement = ({
  content,
  tenant,
  scrollPosition,
  scrollHeight,
  engagementTime,
  playDuration,
  playableMediaType
}) => {
  const scrollPercent = Math.round((scrollPosition / scrollHeight) * 100)
  const { topicName, subjectName } = getContentCategoryFromPath(content.categoryPath)
  const wordCount = countWords(content?.body)
  const [pageType] = content.primaryType

  return {
    verb: VERBS.SCROLL,
    result: {
      duration: engagementTime,
      extensions: {
        wordCount,
        pageScrolled: `${scrollPosition}/${scrollHeight}`,
        pageScrolledPercent: scrollPercent,
        playableMediaType,
        playDuration
      }
    },
    activity: {
      extensions: {
        pageId: content.locationId,
        pageType,
        skillArea: subjectName,
        subSkillArea: topicName
      }
    }
  }
}

/**
 * Create an XAPI event payload.
 * @param {ScrollStatementPayload} payload - XAPI payload.
 * @returns {object}
 */
const create = (payload) => {
  const template =
    payload?.tenant?.features?.xapi?.statementTemplate ||
    XAPI_STATEMENT_TEMPLATES.MT3

  if (template === XAPI_STATEMENT_TEMPLATES.PRIME) {
    return createPrimeStatement(payload)
  }

  if (template === XAPI_STATEMENT_TEMPLATES.HELIX) {
    return createHelixStatement(payload)
  }

  return buildStatement(payload)
}

export default {
  create
}
